import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChipButton } from '@/components/ChipButton';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { SelectionItem } from '@/feature/alerts/components/SelectionSheet';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { months } from '@/feature/transactions/utils/calendar-helper';

const CURRENT_YEAR = new Date().getFullYear();
const EARLIEST_YEAR = 2023; // This was requested by the product team.

export const ALL_MONTHS = 'all';
export type MonthValue = typeof ALL_MONTHS | number;

export type PlayHistoryRangeSelection = {
    fromYear: number;
    fromMonth: MonthValue;
    toYear: number;
    toMonth: MonthValue;
};

const YEAR_ITEMS: SelectionItem[] = Array.from({ length: CURRENT_YEAR - EARLIEST_YEAR + 1 }, (_, index) => {
    const year = (CURRENT_YEAR - index).toString();
    return { id: year, label: year };
});

type DropdownProps = {
    items: SelectionItem[];
    selectedId: string;
    onSelect: (id: string) => void;
};

const Dropdown = ({ items, selectedId, onSelect }: DropdownProps) => {
    const { showSelectionSheet } = useAlerts();
    const selected = items.find(item => item.id === selectedId) ?? items[0];

    const handlePress = () => {
        showSelectionSheet({
            title: '',
            items,
            selection: selected,
            onItemSelected: item => onSelect(item.id),
        });
    };

    return <ChipButton label={selected.label} onPress={handlePress} isDropdown />;
};

type PlayHistoryRangePickerProps = {
    selection: PlayHistoryRangeSelection;
    onChange: (selection: PlayHistoryRangeSelection) => void;
};

export const monthStart = (month: MonthValue) => (month === ALL_MONTHS ? 1 : month);
export const monthEnd = (month: MonthValue) => (month === ALL_MONTHS ? 12 : month);

// Keeps the "to" point from being earlier than the "from" point.
const validateRangeSelection = (next: PlayHistoryRangeSelection): PlayHistoryRangeSelection => {
    if (next.toYear < next.fromYear) {
        return { ...next, toYear: next.fromYear, toMonth: next.fromMonth };
    }
    if (next.toYear === next.fromYear && monthEnd(next.toMonth) < monthStart(next.fromMonth)) {
        return { ...next, toMonth: next.fromMonth };
    }
    return next;
};

export const PlayHistoryRangePicker = ({ selection, onChange }: PlayHistoryRangePickerProps) => {
    const { t } = useTranslation('common');

    const monthItems: SelectionItem[] = [{ id: ALL_MONTHS, label: t('all_months') }, ...months];
    const toMonthId = (month: MonthValue) => (month === ALL_MONTHS ? ALL_MONTHS : `${month - 1}`);
    const toMonthValue = (id: string): MonthValue => (id === ALL_MONTHS ? ALL_MONTHS : Number(id) + 1);

    const handleChange = (partial: Partial<PlayHistoryRangeSelection>) =>
        onChange(validateRangeSelection({ ...selection, ...partial }));

    const toYearItems = YEAR_ITEMS.filter(item => Number(item.id) >= selection.fromYear);
    const toMonthItems =
        selection.toYear === selection.fromYear
            ? monthItems.filter(item => monthEnd(toMonthValue(item.id)) >= monthStart(selection.fromMonth))
            : monthItems;

    return (
        <Box>
            <Row alignItems="center" justifyContent="space-between" mb="s12">
                <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                    {t('from')}
                </Text>
                <Row alignItems="center" gap="s8">
                    <Dropdown
                        items={YEAR_ITEMS}
                        selectedId={`${selection.fromYear}`}
                        onSelect={id => handleChange({ fromYear: Number(id) })}
                    />
                    <Dropdown
                        items={monthItems}
                        selectedId={toMonthId(selection.fromMonth)}
                        onSelect={id => handleChange({ fromMonth: toMonthValue(id) })}
                    />
                </Row>
            </Row>
            <Row alignItems="center" justifyContent="space-between">
                <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                    {t('to')}
                </Text>
                <Row alignItems="center" gap="s8">
                    <Dropdown
                        items={toYearItems}
                        selectedId={`${selection.toYear}`}
                        onSelect={id => handleChange({ toYear: Number(id) })}
                    />
                    <Dropdown
                        items={toMonthItems}
                        selectedId={toMonthId(selection.toMonth)}
                        onSelect={id => handleChange({ toMonth: toMonthValue(id) })}
                    />
                </Row>
            </Row>
        </Box>
    );
};
